// @ts-strict-ignore
import { ProjectHeadVO, ProjectVO } from '../../../model/vo/project.vo';
import { Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { ProjectGroupVO } from '../../../model/vo/project-group.vo';
import { ProjectGroupDataService } from '../../import-export/service/project-group-data.service';
import { ArticleProjectGroupDataService } from '../../article/manager/article-project-group-data.service';
import { NumberUtils } from '@shared/util/number-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectUtils } from '../util/project-utils';
import { ProjectModel } from '../../../model/project.model';
import { Injectable } from '@angular/core';
import { ProjectDataService } from '../../../service/project-data.service';

@Injectable({ providedIn: 'root' })
export class EditProjectService {
  constructor(
  private projectModel: ProjectModel,
  private projectDataService: ProjectDataService,
  private projectGroupDataService: ProjectGroupDataService,
  private articleProjectGroupDataService: ArticleProjectGroupDataService,
  private alertHelper: AlertHelper,
  ) { }

  public renameProject(project: ProjectHeadVO, isArticleProject: boolean, newName: string): Observable<boolean> {
    if (!project || !newName) {
      return of(false);
    }
    if (project.name === newName) {
      // treat this case like project is successfully renamed
      return of(true);
    }

    if (!isArticleProject) {
      // just in case 'undefined' is passed to the method instead of 'false'
      isArticleProject = false;
    }

    return this.loadProjectGroup(project, isArticleProject).pipe(
      flatMap((projectGroup: ProjectGroupVO) => this.checkForDuplicatesAndContinue(projectGroup, project, isArticleProject, newName))
    );
  }

  private loadProjectGroup(project: ProjectHeadVO, isArticleProject: boolean): Observable<ProjectGroupVO> {
    const projectGroup: ProjectGroupVO = project.projectGroup;
    if (!projectGroup || NumberUtils.isNotNumber(projectGroup.id) || projectGroup.id < 0) {
      // don't need to load project-group if project assigned to new (or not existing) project-group
      return of(projectGroup);
    } else {
      if (isArticleProject) {
        return this.articleProjectGroupDataService.loadArticleProjectGroupById(projectGroup.id, 1);
      } else {
        return this.projectGroupDataService.loadProjectGroupById(projectGroup.id, 1);
      }
    }
  }

  private checkForDuplicatesAndContinue(parentProjectGroup: ProjectGroupVO, project: ProjectHeadVO,
                                        isArticleProject: boolean, newName: string): Observable<boolean> {
    const nameExists: boolean = this.nameAlreadyExists(parentProjectGroup, project, newName);
    if (nameExists) {
      this.alertHelper.showError('editProject.title.project_name_in_use', 'editProject.message.project_name_in_use');
      return of(false);
    } else {
      return this.executeRename(project, isArticleProject, newName).pipe(
        map((renamedProject: ProjectVO) => this.handleResponse(project, isArticleProject, renamedProject))
      );
    }
  }

  private nameAlreadyExists(parentProjectGroup: ProjectGroupVO, project: ProjectHeadVO, newName: string): boolean {
    let projectsToCheck: ProjectHeadVO[] = [];
    if (parentProjectGroup && parentProjectGroup.projects) {
      projectsToCheck = parentProjectGroup.projects;
    }

    for (const projectInGroup of projectsToCheck) {
      if (newName === projectInGroup.name && !NumberUtils.equals(project.id, projectInGroup.id)) {
        return true;
      }
    }
    return false;
  }

  private executeRename(project: ProjectHeadVO, isArticleProject: boolean, newName: string): Observable<ProjectHeadVO> {
    if (ProjectUtils.isNewProject(project)) {
      // project doesn't stored in the database yet - rename it locally
      this.setNewName(project, isArticleProject, newName);
      return of(project);
    } else {
      if (isArticleProject) {
        return this.projectDataService.renameCompositeArticleProject(project.id, newName);
      } else {
        return this.projectDataService.renameProject(project.id, newName);
      }
    }
  }

  private handleResponse(project: ProjectHeadVO, isArticleProject: boolean, response: ProjectVO): boolean {
    if (!response) {
      this.alertHelper.showError('global.label.rename', 'main.message.rename_project_does_not_exist_error');
      return false;
    }

    this.setNewName(project, isArticleProject, response.name);
    return true;
  }

  private setNewName(project: ProjectHeadVO, isArticleProject: boolean, newName: string): void {
    this.projectModel.rename(project, newName);
  }
}
